import { keyBy } from "lodash";
import { createSelector } from "reselect";
import { createTable } from "robodux";
import { call, put, select } from "saga-query";
import { sleep as fxSleep, takeLeading } from "starfx";

import { encryptPayoad } from "@app/service/helpers";
import { isOk } from "@app/statefx/helpers";
import { runThunk, safeApi } from "@app/statefx/helpers/fxhelpers";
import { schema } from "@app/statefx/schema";
import { xouterApi, xThunk } from "@app/statefx/xapis";
import { sfxNotify } from "@app/statefx/xThunks/notify";

import { api, thunks } from "../apis/api";
import { suGroups } from "./admins";
import { appNotify } from "./notify";

import type {
  HostedOnboardingRequest,
  HostedOnboardingSummary,
  IGroupMember,
  OrganizationCommercialProfile,
  TGroupTreeListItem,
} from "@app/types";
import type { RootState } from "@app/state/rootState";
import type { ApiCtx } from "saga-query";
type TReqPerseed = {
  PERSEED: string;
};

const REP_MALINK = "ui/malink";

type TMalink = {
  ID: number;
  CODE: string;
};
export const malinkRepo = createTable<TMalink>({
  name: REP_MALINK,
  initialState: {} as Record<TMalink["ID"], TMalink>,
});

const REPO_GROUPS_AVAILABLE = "groups/all-avaliable";

const newGroup: IGroupMember = {
  GROUPID: -1,
  GROUP_NAME: "",
  PERSEED: "",
  RDBKEY: "",
  SEED: "JGAP",
  OWNER_AMA: 0,
  OWNER_NAME: "",
  OWNER_EMAIL: "",
  ME_NAME: "",
  ME_EMAIL: "",
  ME_AMAXAVO: 0,
  ME_RIGHTS: "",
  IS_OWNER: 0,
  IS_DEFAULT: 0,
  IS_HOME: 0,
  IS_ACTIVE: 0,
  INTERNAL_CEID: 0,
  ME_STATUS: "",
  ME_STATUSOP: new Date().toISOString(),
  DIR_UPDATE_PRIMARY: "",
  DIR_UPDATE_SECONDARY: "",
  GROUP_CREATED: new Date().toISOString(),
  INSIDERS_READY: false,
  DBAUID: "",
  DBSRV_ABR: "LOCAL",
  KIT_SRV: "APP",
  LICENSE_SHORT_NAME: "",
  touched: true,
};

export const groupsRepo = createTable<IGroupMember>({
  name: REPO_GROUPS_AVAILABLE,
  initialState: {},
});

export const selectHostedOnboardings = schema.hostedOnboardings.selectTableAsList;

export const selectHostedOnboardingById = (state: unknown, id?: string) =>
  id ? schema.hostedOnboardings.selectById(state, { id }) || null : null;

export const canGenerateTemporaryToken = (
  treeItem: Pick<TGroupTreeListItem, "id" | "ptlLifecycle"> | undefined,
  group: IGroupMember | undefined
) =>
  treeItem?.ptlLifecycle === "FINALIZED" &&
  typeof treeItem.id === "number" &&
  treeItem.id > 0 &&
  group?.GROUPID === treeItem.id &&
  group.GROUPID > 0;

export const listHostedOnboardings = xouterApi.get(
  "xapi/groups/listHostedOnboardings",
  { supervisor: takeLeading },
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: "/swsbuilder/v2/moa/onboardings",
      credentials: "include",
    });
    yield* next();
    if (isOk(ctx.json) && Array.isArray(ctx.json.value)) {
      yield* schema.update(
        schema.hostedOnboardings.set(keyBy(ctx.json.value, "id"))
      );
    }
    return ctx.json;
  }
);

export const readHostedOnboarding = xouterApi.get<{
  id: string;
}>(
  "xapi/groups/readHostedOnboarding",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/details`,
      credentials: "include",
    });
    yield* next();
    if (isOk(ctx.json) && ctx.json.value?.id) {
      yield* schema.update(
        schema.hostedOnboardings.patch({
          [ctx.json.value.id]: ctx.json.value,
        })
      );
    }
    return ctx.json;
  }
);

export const checkHostedOnboardingPerseed = xouterApi.get<{
  perseed: string;
}>(
  "xapi/groups/checkHostedOnboardingPerseed",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: `/su/groups/gdcheck?perseed=${encodeURIComponent(
        ctx.payload.perseed
      )}`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

export const suggestHostedOnboardingPerseed = xouterApi.get<{
  prefix: string;
}>(
  "xapi/groups/suggestHostedOnboardingPerseed",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: `/su/groups/gseedsuggest?prefix=${encodeURIComponent(
        ctx.payload.prefix
      )}`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

const createHostedOnboarding = xouterApi.post<HostedOnboardingRequest>(
  "xapi/groups/createHostedOnboarding",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: "/swsbuilder/v2/moa/onboardings",
      credentials: "include",
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
    return ctx.json;
  }
);

const reserveHostedOnboardingRequest = xouterApi.post<{ id: string }>(
  "xapi/groups/reserveHostedOnboarding",
  function* (ctx, next) {
  ctx.request = ctx.req({
    method: "POST",
    url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/reserve`,
    credentials: "include",
  });
  yield* next();
    return ctx.json;
  }
);

const retryHostedOnboardingRequest = xouterApi.post<{ id: string }>(
  "xapi/groups/retryHostedOnboarding",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/retry`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

const approveHostedOnboardingRequest = xouterApi.post<{ id: string }>(
  "xapi/groups/approveHostedOnboarding",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/approve`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

type HostedOnboardingOverride = Pick<
  HostedOnboardingRequest,
  "clientCode" | "organization"
> & {
  id: string;
};

const overrideHostedOnboardingRequest =
  xouterApi.patch<HostedOnboardingOverride>(
    "xapi/groups/overrideHostedOnboarding",
    function* (ctx, next) {
      const { id, ...override } = ctx.payload;
      ctx.request = ctx.req({
        method: "PATCH",
        url: `/swsbuilder/v2/moa/onboardings/${id}/override`,
        credentials: "include",
        body: JSON.stringify(override),
      });
      yield* next();
      return ctx.json;
    }
  );

const abandonHostedOnboardingRequest = xouterApi.post<{
  id: string;
  reason: string;
}>("xapi/groups/abandonHostedOnboarding", function* (ctx, next) {
  ctx.request = ctx.req({
    method: "POST",
    url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/abandon`,
    credentials: "include",
    body: JSON.stringify({ reason: ctx.payload.reason }),
  });
  yield* next();
  return ctx.json;
});

const resendNotificationRequest = xouterApi.post<{ id: string }>(
  "xapi/groups/resendNotification",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: `/swsbuilder/v2/moa/onboardings/${ctx.payload.id}/resend-notification`,
      credentials: "include",
    });
    yield* next();
    return ctx.json;
  }
);

function* notifyOnboardingError(message: string) {
  yield* runThunk(sfxNotify, {
    type: "error",
    message,
    duration: 4000,
  });
}

export const reserveHostedOnboarding = xThunk.create<{ id: string }>(
  "xthunk/groups/reserveHostedOnboarding",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<HostedOnboardingSummary, { id: string }>(
      reserveHostedOnboardingRequest,
      ctx.payload
    );
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Onboarding failed"
      );
      return;
    }
    yield* safeApi(readHostedOnboarding, ctx.payload);
  }
);

export const retryHostedOnboarding = xThunk.create<{ id: string }>(
  "xthunk/groups/retryHostedOnboarding",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<HostedOnboardingSummary, { id: string }>(
      retryHostedOnboardingRequest,
      ctx.payload
    );
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Onboarding failed"
      );
      return;
    }
    yield* safeApi(readHostedOnboarding, ctx.payload);
  }
);

export const approveHostedOnboarding = xThunk.create<{ id: string }>(
  "xthunk/groups/approveHostedOnboarding",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<HostedOnboardingSummary, { id: string }>(
      approveHostedOnboardingRequest,
      ctx.payload
    );
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Approval failed"
      );
      return;
    }
    yield* safeApi(readHostedOnboarding, ctx.payload);
    yield* listHostedOnboardings.run(listHostedOnboardings());
  }
);

export const overrideHostedOnboarding = xThunk.create<HostedOnboardingOverride>(
  "xthunk/groups/overrideHostedOnboarding",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<
      HostedOnboardingSummary,
      HostedOnboardingOverride
    >(overrideHostedOnboardingRequest, ctx.payload);
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Override failed"
      );
      return;
    }
    yield* safeApi(readHostedOnboarding, { id: ctx.payload.id });
    yield* listHostedOnboardings.run(listHostedOnboardings());
  }
);

export const abandonHostedOnboarding = xThunk.create<{
  id: string;
  reason: string;
}>(
  "xthunk/groups/abandonHostedOnboarding",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<
      HostedOnboardingSummary,
      { id: string; reason: string }
    >(abandonHostedOnboardingRequest, ctx.payload);
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Abandonment failed"
      );
      return;
    }
    yield* schema.update(
      schema.hostedOnboardings.patch({
        [result.value.id]: result.value,
      })
    );
    yield* listHostedOnboardings.run(listHostedOnboardings());
  }
);

export const resendNotification = xThunk.create<{ id: string }>(
  "xthunk/groups/resendNotification",
  { supervisor: takeLeading },
  function* (ctx) {
    const result = yield* safeApi<HostedOnboardingSummary, { id: string }>(
      resendNotificationRequest,
      ctx.payload
    );
    if (!result.ok) {
      yield* notifyOnboardingError(
        "error" in result ? result.error.message : "Notification resend failed"
      );
      return;
    }
    yield* safeApi(readHostedOnboarding, ctx.payload);
  }
);

export const createHostedOrganization = xThunk.create<HostedOnboardingRequest>(
  "xthunk/groups/createHostedOrganization",
  { supervisor: takeLeading },
  function* (ctx) {
    const created = yield* safeApi<
      HostedOnboardingSummary,
      HostedOnboardingRequest
    >(createHostedOnboarding, ctx.payload);
    if (!created.ok) {
      yield* notifyOnboardingError(
        "error" in created ? created.error.message : "Onboarding failed"
      );
      return;
    }
    const draft = {
      ...created.value,
      request: ctx.payload,
      attempts: [],
    };
    yield* schema.update(
      schema.hostedOnboardings.set({ [draft.id]: draft })
    );
    yield* schema.update(schema.hostedOnboardingSelection.set(draft.id));
    yield* reserveHostedOnboarding.run(
      reserveHostedOnboarding({ id: draft.id })
    );
  }
);

export const clearHostedOnboardingSelection = xThunk.create(
  "xthunk/groups/clearHostedOnboardingSelection",
  function* () {
    yield* schema.update(schema.hostedOnboardingSelection.set(""));
  }
);

export const pollHostedOnboarding = xThunk.create<{ id: string }>(
  "xthunk/groups/pollHostedOnboarding",
  function* (ctx) {
    while (true) {
      const result = yield* safeApi<HostedOnboardingSummary, { id: string }>(
        readHostedOnboarding,
        ctx.payload
      );
      if (
        !result.ok ||
        ["SERVER_READY", "MANUAL_REVIEW_REQUIRED", "ABANDONED"].includes(
          result.value.status
        )
      ) {
        return;
      }
      yield* fxSleep(5000);
    }
  }
);

// let once = false;

export const setGroupData = thunks.create<
  Record<IGroupMember["GROUPID"], IGroupMember>
>("setGroupData", function* (ctx, next) {
  yield* put(groupsRepo.actions.set(ctx.payload));
  yield* next();
});

export const resetGroupData = thunks.create(
  "resetGroupData",
  function* (ctx, next) {
    yield* put(groupsRepo.actions.reset());
    yield* next();
  }
);

export const addNewGroup = thunks.create(
  "groups/addGroup",
  function* (ctx, next) {
    yield* put(groupsRepo.actions.add({ [newGroup.GROUPID]: { ...newGroup } }));
    yield* next();
  }
);

export const removeNewGroup = thunks.create(
  "groups/removeNewGroup",
  function* (ctx, next) {
    yield* put(groupsRepo.actions.remove(["-1"]));
    yield* next();
  }
);

type OrganizationCommercialProfileTarget = {
  GROUPID: number;
  PERSEED: string;
};

const getOrganizationCommercialProfile = api.get<OrganizationCommercialProfileTarget>(
  "groups/getOrganizationCommercialProfile",
  function* (ctx, next) {
    ctx.request = ctx.req({
      url: `/su/groups/${encodeURIComponent(
        ctx.payload.PERSEED
      )}/commercial-profile`,
      credentials: "include",
    });
    yield* next();
  }
);

export const loadOrganizationCommercialProfile = thunks.create<OrganizationCommercialProfileTarget>(
  "groups/loadOrganizationCommercialProfile",
  function* (ctx, next) {
    const request = yield* call(
      getOrganizationCommercialProfile.run,
      getOrganizationCommercialProfile(ctx.payload)
    );
    const { data, ok } = request.json || {};
    if (ok) {
      yield* put(
        groupsRepo.actions.patch({
          [ctx.payload.GROUPID]: { commercialProfile: data },
        })
      );
    }
    yield* next();
  }
);

type OrganizationCommercialProfileUpdate = OrganizationCommercialProfileTarget & {
  profile: OrganizationCommercialProfile;
};

const postOrganizationCommercialProfile = api.post<OrganizationCommercialProfileUpdate>(
  "groups/postOrganizationCommercialProfile",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: `/su/groups/${encodeURIComponent(
        ctx.payload.PERSEED
      )}/commercial-profile`,
      credentials: "include",
      body: JSON.stringify(ctx.payload.profile),
    });
    yield* next();
  }
);

export const updateOrganizationCommercialProfile = thunks.create<OrganizationCommercialProfileUpdate>(
  "groups/updateOrganizationCommercialProfile",
  function* (ctx, next) {
    const request = yield* call(
      postOrganizationCommercialProfile.run,
      postOrganizationCommercialProfile(ctx.payload)
    );
    const { data, ok } = request.json || {};
    if (ok) {
      yield* put(
        groupsRepo.actions.patch({
          [ctx.payload.GROUPID]: { commercialProfile: data },
        })
      );
      yield* call(
        appNotify.run,
        appNotify({
          message: "Organization profile updated",
          type: "success",
          position: "bottom",
        })
      );
    }
    yield* next();
  }
);
export const updateGroup = thunks.create<IGroupMember>(
  "groups/updateGroup",
  function* (ctx, next) {
    if (!ctx?.payload?.GROUPID) {
      return;
    }
    const thepost = yield* call(
      postUpdateGroup.run,
      postUpdateGroup(ctx.payload)
    );
    const { data, ok } = thepost.json;
    if (!ok) {
      return null;
    }
    yield* put(
      groupsRepo.actions.patch({ [ctx.payload.GROUPID]: { ...ctx.payload } })
    );
    // reload the full list //
    yield* call(suGroups.run, suGroups());
    yield* next();
  }
);

function buildUpdate(
  allowedFields: Array<keyof IGroupMember>,
  prev: IGroupMember,
  next: IGroupMember
) {
  const groupId = next.GROUPID;
  const updateFields = [];
  // Iterate over each property in the 'next' object
  for (const key in next) {
    if (
      key !== "touched" &&
      key !== "GROUPID" &&
      prev[key as keyof IGroupMember] !== next[key as keyof IGroupMember] &&
      allowedFields.includes(key as keyof IGroupMember)
    ) {
      // Add the updated fields to the updateFields array
      updateFields.push(`${key} = '${next[key as keyof IGroupMember]}'`);
    }
  }
  // Construct the SQL update statement
  const query = `UPDATE ALL_GROUP_SEEDS SET ${updateFields.join(
    ", "
  )} WHERE AGS_ID = ${groupId} RETURNING AGS_ID;`;
  return {
    q: query,
    f: updateFields,
    k: groupId,
  };
}

/**
 * This function is a workaround for when we moved the
 * DBSRV_ABR field to CRT_MACHINE into new table DBSRV_ABR!!!
 * actually it updates the CRT_MACHINE field in the DBSRV_ABR table and that's it
 * On the backend only the "q" parameter is used
 * @date 8/17/2023 - 4:49:24 PM
 *
 * @param {Array<keyof IGroupMember>} allowedFields
 * @param {IGroupMember} prev
 * @param {IGroupMember} next
 * @returns {{ q: string; f: {}; k: any; }}
 */
function buildUpdate2(
  allowedFields: Array<keyof IGroupMember>,
  prev: IGroupMember,
  next: IGroupMember
) {
  // const groupId = next.GROUPID;
  const updateFields = [];
  const weUpdateThis = [];
  // Iterate over each property in the 'next' object
  for (const key in next) {
    const aliasField = key;
    let realField = key;
    if (key === "DBSRV_ABR") {
      realField = "CRT_MACHINE";
    }

    if (
      key !== "touched" &&
      key !== "GROUPID" &&
      prev[aliasField as keyof IGroupMember] !==
        next[aliasField as keyof IGroupMember] &&
      allowedFields.includes(aliasField as keyof IGroupMember)
    ) {
      // Add the updated fields to the updateFields array
      updateFields.push(
        `${realField} = '${next[aliasField as keyof IGroupMember]}'`
      );
      weUpdateThis.push(aliasField);
    }
  }
  // Construct the SQL update statement
  const query = `UPDATE BDEF_DBS SET ${updateFields.join(
    ", "
  )} WHERE PERSEED = '${next.PERSEED}' AND DBSYS_NAME = 'FIREBIRD' returning ${
    next.GROUPID
  } AS AGS_ID;`;
  return {
    q: query,
    f: weUpdateThis, // removed from backend
    k: null, // removed from backend
  };
}
const postUpdateGroup = api.post<IGroupMember>(
  "groups/postUpdateGroup",
  function* (ctx: ApiCtx<any, IGroupMember>, next) {
    const prevDataset = yield* select(selectedGroupData, ctx.payload.GROUPID);
    const O = {
      q: "",
      f: [],
      k: 0,
    };
    if (!prevDataset) {
      return;
    }
    if (prevDataset.GROUP_NAME != ctx.payload.GROUP_NAME) {
      Object.assign(
        O,
        buildUpdate(["GROUP_NAME", "DBSRV_ABR"], prevDataset, ctx.payload)
      );
    }

    if (prevDataset.DBSRV_ABR != ctx.payload.DBSRV_ABR) {
      Object.assign(O, buildUpdate2(["DBSRV_ABR"], prevDataset, ctx.payload));
    }

    const { q, f, k } = O;
    if (!f.length) {
      return;
    }
    //we build the query on server side
    ctx.request = ctx.req({
      method: "POST",
      url: "/su/groups/gdupdate",
      body: JSON.stringify(encryptPayoad({ q, f, k })),
    });
    yield* next();
  }
);

// create new client
export const createNewGroup = api.post<IGroupMember>(
  "groups/createNewGroup",
  function* (ctx: ApiCtx<any, IGroupMember>, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: "/su/groups/gdnew",
      body: JSON.stringify(encryptPayoad({ ...ctx.payload, IS_ACTIVE: true })),
    });
    // create client entry in MOA
    yield* next();
    const { data, ok } = yield ctx.json;
    if (!ok) {
      return null;
    }
    // create direcrectory structure
    if (data) {
      yield* call(suGroups.run, suGroups());
    }
    return data;
    //
    // back to ui
    //
  }
);

export const getTemporaryToken = api.post<{ GROUPID: number }>(
  "groups/getTemporaryToken",
  function* (ctx, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: "/remoteworks/gdtoken",
      body: JSON.stringify(encryptPayoad({ ...ctx.payload })),
    });
    yield* next();
    const { data, ok } = yield ctx.json;
    if (!ok) {
      return null;
    }
    yield* put(
      malinkRepo.actions.set({
        [ctx.payload.GROUPID]: {
          ID: ctx.payload.GROUPID,
          CODE: data?.code,
        },
      })
    );
  }
);

/* -------------------------------------------------------------------------- */

function createParameterSelector(selector: any) {
  return (_: any, params: any) => selector(params);
}
const getGroupid = createParameterSelector(
  (params: any) => params?.GROUPID || null
);

const selectors = groupsRepo.getSelectors(
  (state: RootState) => state[REPO_GROUPS_AVAILABLE]
);

export const selectAllGroupsList = createSelector(
  selectors.selectTable,
  (t) => t
);

export const selectAllGroupsListArray = createSelector(
  selectors.selectTableAsList,
  (t) => t
);

export const selectedGroupData = (
  state: RootState,
  groupId: number | string | undefined
) =>
  selectors.selectById(state, { id: `${groupId}` }) || ({} as IGroupMember);

//magic link//
const maselectors = malinkRepo.getSelectors(
  (state: RootState) => state[REP_MALINK]
);
export const selectMaLinkByGroupId = createSelector(
  maselectors.selectTable,
  getGroupid,
  (t, id) => {
    return t[id] as TMalink;
  }
);

export const selectHotAccess = createSelector(
  selectAllGroupsListArray,

  (groups): string[] => {
    // to do, cross check with the dbperseeds //

    return (
      groups
        /* the procedure in the database filters out the databases without insiders activated,
      so we don't need to do it here.
      We could do however a check to see if the INSIDERS_READY  is true but no AMAXAVO,
      THAT MEANING AN INACTIVE INSIDER BUTTON,
      Or perhaps we should display a panel to inform the user that he/she needs to enable the email in the firebird database
      */
        .filter((g) => g.INSIDERS_READY === true)
        .map((g) => g.PERSEED)
    );
  }
);
